import React, { useMemo, useState } from 'react';
import {
  DEFAULT_DEVICE_MODELS,
  DEFAULT_DOCUMENT_TYPE,
  MAX_MANUAL_BYTES,
} from '../../helper/DeviceDocumentsHelper';

const formatBytes = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb >= 10 ? mb.toFixed(0) : mb.toFixed(1)} MB`;
};

const UploadManualModal = ({
  models = DEFAULT_DEVICE_MODELS,
  defaultModel,
  busy = false,
  onClose,
  onSubmit,
}) => {
  const [deviceModel, setDeviceModel] = useState(defaultModel || models[0] || 'V730i');
  const [version, setVersion] = useState('');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  const productOptions = models.length ? models : DEFAULT_DEVICE_MODELS;

  const fileLabel = useMemo(() => {
    if (!file) return 'PDF, up to 100 MB';
    return `${file.name} · ${formatBytes(file.size)}`;
  }, [file]);

  const validate = () => {
    const next = {};
    if (!file) next.file = 'Choose a PDF.';
    else if (file.type && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      next.file = 'Only PDF files are allowed.';
    } else if (file.size > MAX_MANUAL_BYTES) {
      next.file = 'File must be 100 MB or smaller.';
    }
    if (!deviceModel) next.deviceModel = 'Select a product.';
    if (!String(version).trim()) next.version = 'Enter a version, e.g. 4.2.';
    if (!String(title).trim()) next.title = 'Enter a title.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFile = (nextFile) => {
    setFile(nextFile || null);
    setErrors((prev) => ({ ...prev, file: undefined }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (busy || !validate()) return;
    onSubmit({
      file,
      deviceModel,
      version: version.trim(),
      title: title.trim(),
      documentType: DEFAULT_DOCUMENT_TYPE,
    });
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="docs-upload-title">
      <div className="backdrop" onClick={busy ? undefined : onClose} aria-hidden="true" />
      <div className="modal-content docs-modal-wide">
        <h2 id="docs-upload-title">Upload user manual</h2>
        <p className="docs-modal-copy">
          Processing starts right after upload. Publishing as the product default is a separate step.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label className="docs-file-drop">
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => handleFile(event.target.files?.[0])}
                disabled={busy}
              />
              <strong>{file ? file.name : 'Choose PDF'}</strong>
              <span>{fileLabel}</span>
            </label>
            {errors.file ? <p className="error">{errors.file}</p> : null}
          </div>

          <div className="input-group">
            <select
              value={deviceModel}
              onChange={(event) => setDeviceModel(event.target.value)}
              disabled={busy}
              aria-label="Product"
            >
              {productOptions.map((model) => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
            {errors.deviceModel ? <p className="error">{errors.deviceModel}</p> : null}
          </div>

          <div className="input-group">
            <input
              type="text"
              value={version}
              onChange={(event) => setVersion(event.target.value)}
              placeholder="Version, e.g. 4.2"
              disabled={busy}
            />
            {errors.version ? <p className="error">{errors.version}</p> : null}
          </div>

          <div className="input-group">
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Title, e.g. V730i User Manual"
              disabled={busy}
            />
            {errors.title ? <p className="error">{errors.title}</p> : null}
          </div>

          <div className="actions">
            <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" disabled={busy}>
              {busy ? 'Uploading…' : 'Upload and process'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadManualModal;
